import {transactionFingerprint} from './transaction-fingerprint';
import {studyReferenceExists} from './study-service';
import { type Table } from "dexie";
import {
  NookDatabase,
  assertWritable,
  markSearchDirty,
} from "../infrastructure/database/database";
import { assertCapacity } from "../infrastructure/backup/snapshot";
import { collectAttachments, collectTags } from "./attachment-gc";
import {
  contentSchemas,
  contentDraftSchemas,
  contentDraftSchema,
  contentTables,
  contentKinds,
  type ContentKind,
  type Content,
  type ContentDraft,
} from "../domain/content/model";
import type { PreparedAttachment } from "../domain/capture/model";
export class ContentService {
  constructor(readonly db: NookDatabase) {}
  table(kind: ContentKind): Table<Content, string> {
    return this.db.table(contentTables[kind]);
  }
  async get(kind: ContentKind, id: string) {
    return this.table(kind).get(id);
  }
  async all() {
    return (
      await Promise.all(
        contentKinds.map(async (kind) =>
          (await this.table(kind).toArray()).map((item) => ({ kind, item })),
        ),
      )
    ).flat();
  }
  private async command<T>(
    kind: "content-save" | "content-draft" | "content-state",
    op: string,
    input: unknown,
    run: () => Promise<{ value: T; id: string; revision: number }>,
    duplicate: (id: string) => Promise<T>,
  ) {
    const fingerprint = await transactionFingerprint(input);
    return this.db.transaction("rw", this.db.tables, async () => {
      await assertWritable(this.db);
      const done = await this.db.operations.get(op);
      if (done) {
        if (done.kind !== kind || done.fingerprint !== fingerprint)
          throw Error("操作标识已用于不同输入");
        return duplicate(done.resultId);
      }
      const r = await run();
      await this.db.operations.add({
        id: op,
        kind,
        fingerprint,
        resultId: r.id,
        resultRevision: r.revision,
        committedAt: new Date().toISOString(),
      });
      await markSearchDirty(this.db);
      await assertCapacity(this.db);
      return r.value;
    });
  }
  private async history(
    kind: ContentKind,
    item: Content,
    reason: "created" | "edited" | "trashed" | "restored" | "history-restored",
  ) {
    await this.db.revisions.add({
      id: `${kind}:${item.id}:${item.revision}`,
      entityType: kind,
      entityId: item.id,
      revision: item.revision,
      reason,
      recordedAt: item.updatedAt,
      snapshot: item,
    });
  }
  private async attachments(ids: string[]) {
    if (new Set(ids).size !== ids.length) throw Error("图片引用重复");
    for (const id of ids) {
      const a = await this.db.attachments.get(id);
      if (!a || !(await this.db.attachmentBlobs.get(a.sha256)))
        throw Error("图片尚未保存；文字草稿保留");
    }
  }
  async draft(
    kind: ContentKind,
    payload: Content,
    baseRevision: number,
    id: string,
    expected: number,
    source: { captureId: string; revision: number } | null = null,
    prepared: PreparedAttachment[] = [],
    tagNames?: string[],
  ) {
    return this.command(
      "content-draft",
      crypto.randomUUID(),
      { kind, payload, baseRevision, id, expected, source },
      async () => {
        contentDraftSchemas[kind].parse(payload);
        if (tagNames) {
          if (
            tagNames.length > 30 ||
            tagNames.some((n) => !n.trim() || n.length > 100)
          )
            throw Error("标签格式或数量无效");
          const tagIds: string[] = [];
          for (const raw of tagNames) {
            const name = raw.trim(),
              normalized = name.normalize("NFKC").toLocaleLowerCase(),
              old = await this.db.tags
                .where("normalized")
                .equals(normalized)
                .first();
            const tag = old ?? { id: crypto.randomUUID(), name, normalized };
            if (!old) await this.db.tags.add(tag);
            if (!tagIds.includes(tag.id)) tagIds.push(tag.id);
          }
          payload = { ...payload, tagIds };
        }
        for (const id of payload.tagIds)
          if (!(await this.db.tags.get(id))) throw Error("标签引用缺失");
        if (baseRevision && !(await this.get(kind, payload.id)))
          throw Error("草稿原对象缺失");
        if (source && !(await this.db.captures.get(source.captureId)))
          throw Error("整理来源缺失");
        const old = await this.db.contentDrafts.get(id);
        if (old && (old.targetId !== payload.id || old.kind !== kind))
          throw Error("草稿归属不一致");
        for (const p of prepared) {
          if (!payload.attachmentIds.includes(p.metadata.id)) continue;
          if (
            p.metadata.sha256 !== p.data.sha256 ||
            p.metadata.bytes !== p.data.blob.size ||
            p.data.bytes !== p.metadata.bytes
          )
            throw Error("图片保存失败；文字仍保留");
          const a = await this.db.attachments.get(p.metadata.id);
          if (a && a.sha256 !== p.metadata.sha256) throw Error("图片 ID 冲突");
          await this.db.attachments.put(p.metadata);
          if (!(await this.db.attachmentBlobs.get(p.data.sha256)))
            await this.db.attachmentBlobs.add(p.data);
        }
        await this.attachments(payload.attachmentIds);
        const fork = !!old?.deletedAt || (old?.revision ?? 0) !== expected,
          now = new Date().toISOString();
        const d = contentDraftSchema.parse({
          id: fork ? crypto.randomUUID() : id,
          targetId: payload.id,
          kind,
          baseRevision,
          payload,
          sourceCaptureId: source?.captureId ?? old?.sourceCaptureId ?? null,
          sourceRevision: source?.revision ?? old?.sourceRevision ?? 0,
          revision: fork ? 1 : expected + 1,
          createdAt: fork ? now : (old?.createdAt ?? now),
          updatedAt: now,
          deletedAt: null,
        });
        await this.db.contentDrafts.put(d);
        await collectTags(this.db);
        return { value: d, id: d.id, revision: d.revision };
      },
      async (id) => (await this.db.contentDrafts.get(id))!,
    );
  }
  async publish(draftId: string, revision: number, op: string, asCopy = false) {
    return this.command(
      "content-save",
      op,
      { draftId, revision, asCopy },
      async () => {
        const d = await this.db.contentDrafts.get(draftId);
        if (!d || d.deletedAt || d.revision !== revision)
          throw Error("草稿已变化，未覆盖");
        const previous = await this.get(d.kind, d.targetId);
        if (
          !asCopy &&
          (previous?.deletedAt || (previous?.revision ?? 0) !== d.baseRevision)
        )
          throw Error("另一处已有新版本；本次草稿保留，未覆盖");
        if (!previous && !asCopy) {
          const ids = [
            ...(await this.db.manualStudyPlans.toArray()),...(await this.db.manualStudySessions.toArray()),...(await this.db.sopTemplates.toArray()),...(await this.db.sopRuns.toArray()),...(await this.db.captures.toArray()),
            ...(await this.db.tasks.toArray()),
            ...(await this.db.events.toArray()),
            ...(await this.db.projects.toArray()),
            ...(await this.all()).map((x) => x.item),
          ];
          if (ids.some((x) => x.id === d.targetId))
            throw Error("稳定 ID 已被使用");
        }
        const now = new Date().toISOString();
        const item = contentSchemas[d.kind].parse({
          ...d.payload,
          id: asCopy ? crypto.randomUUID() : d.targetId,
          revision: asCopy ? 1 : d.baseRevision + 1,
          createdAt: asCopy
            ? now
            : (previous?.createdAt ?? d.payload.createdAt),
          updatedAt: now,
          archivedAt: asCopy ? null : (previous?.archivedAt ?? null),
          deletedAt: null,
        });
        await this.attachments(item.attachmentIds);
        if (d.sourceCaptureId && !asCopy) {
          const c = await this.db.captures.get(d.sourceCaptureId);
          if (!c || c.deletedAt || c.revision !== d.sourceRevision)
            throw Error("整理来源已变化");
          if (
            (
              await this.db.relations.where("captureId").equals(c.id).toArray()
            ).some((r) => !r.revokedAt)
          )
            throw Error("原记录已经整理");
          await this.db.relations.add({
            id: op,
            kind: "derived",
            captureId: c.id,
            targetType: d.kind,
            targetId: item.id,
            createdAt: now,
            revokedAt: null,
          });
          const next = {
            ...c,
            state: "organized" as const,
            revision: c.revision + 1,
            updatedAt: now,
          };
          await this.db.captures.put(next);
          await this.db.revisions.add({
            id: `${c.id}:${next.revision}`,
            captureId: c.id,
            revision: next.revision,
            reason: "organized",
            recordedAt: now,
            snapshot: next,
          });
        }
        await this.table(d.kind).put(item);
        await this.history(
          d.kind,
          item,
          previous && !asCopy ? "edited" : "created",
        );
        await this.db.contentDrafts.delete(d.id);
        await collectTags(this.db);
        return { value: item, id: item.id, revision: item.revision };
      },
      async (id) => {
        for (const { item } of await this.all())
          if (item.id === id) return item;
        throw Error("原提交对象不存在，未重复创建");
      },
    );
  }
  async lifecycle(
    kind: ContentKind,
    id: string,
    rev: number,
    action: "archive" | "unarchive" | "trash" | "restore" | "purge",
    op: string,
  ) {
    return this.command(
      "content-state",
      op,
      { kind, id, rev, action },
      async () => {
        const old = await this.get(kind, id);
        if (!old || old.revision !== rev) throw Error("对象已变化");
        if (action === "purge") {
          if (!old.deletedAt) throw Error("只能删除回收站内容");
          if(kind==='resource'&&await studyReferenceExists(this.db,'resource',id))throw Error("资料仍被自主学习、草稿或历史引用");
          if (await this.db.contentDrafts.where("targetId").equals(id).count())
            throw Error("仍有编辑草稿");
          if (
            (
              await this.db.relations
                .where("[targetType+targetId]")
                .equals([kind, id])
                .toArray()
            ).some((r) => !r.revokedAt)
          )
            throw Error("请先撤销原记录整理");
          await this.table(kind).delete(id);
          await this.db.revisions
            .where("[entityType+entityId]")
            .equals([kind, id])
            .delete();
          await collectAttachments(this.db);
        } else {
          if (["archive", "unarchive"].includes(action) && old.deletedAt)
            throw Error("请先恢复回收站内容");
          if (
            (action === "restore" && !old.deletedAt) ||
            (action === "trash" && old.deletedAt)
          )
            throw Error("状态已变化");
          const now = new Date().toISOString(),
            next = {
              ...old,
              revision: rev + 1,
              updatedAt: now,
              ...(action === "archive" || action === "unarchive"
                ? { archivedAt: action === "archive" ? now : null }
                : { deletedAt: action === "trash" ? now : null }),
            };
          await this.table(kind).put(next);
          await this.history(
            kind,
            next,
            action === "trash"
              ? "trashed"
              : action === "restore"
                ? "restored"
                : "edited",
          );
        }
        return { value: undefined, id, revision: rev + 1 };
      },
      async () => undefined,
    );
  }
  async draftLifecycle(
    id: string,
    revision: number,
    action: "trash" | "restore" | "purge",
    op: string,
  ) {
    return this.command(
      "content-state",
      op,
      { id, revision, action },
      async () => {
        const d = await this.db.contentDrafts.get(id);
        if (!d || d.revision !== revision) throw Error("草稿已变化");
        if (action === "purge") {
          if (!d.deletedAt) throw Error("请先移入回收站");
          await this.db.contentDrafts.delete(id);
          await collectAttachments(this.db);
        } else {
          if ((action === "trash") === !!d.deletedAt)
            throw Error("草稿状态已变化");
          await this.db.contentDrafts.put({
            ...d,
            deletedAt: action === "trash" ? new Date().toISOString() : null,
            revision: revision + 1,
            updatedAt: new Date().toISOString(),
          });
        }
        return { value: undefined, id, revision: revision + 1 };
      },
      async () => undefined,
    );
  }
  async restoreHistory(
    kind: ContentKind,
    id: string,
    currentRevision: number,
    historyRevision: number,
    op: string,
  ) {
    return this.command(
      "content-save",
      op,
      { kind, id, currentRevision, historyRevision },
      async () => {
        const current = await this.get(kind, id),
          r = await this.db.revisions.get(`${kind}:${id}:${historyRevision}`);
        if (
          !current ||
          current.deletedAt ||
          current.revision !== currentRevision ||
          !r ||
          !("entityType" in r) ||
          r.entityType !== kind
        )
          throw Error("历史或当前对象已变化");
        const item = contentSchemas[kind].parse({
          ...r.snapshot,
          createdAt: current.createdAt,
          archivedAt: current.archivedAt,
          deletedAt: null,
          revision: currentRevision + 1,
          updatedAt: new Date().toISOString(),
        });
        await this.attachments(item.attachmentIds);
        await this.table(kind).put(item);
        await this.history(kind, item, "history-restored");
        return { value: item, id, revision: item.revision };
      },
      async (id) => (await this.get(kind, id))!,
    );
  }
}
/** One authoritative overlay shared by lists, detail and search. Conflicting branches are separate. */
export async function visibleContent(db: NookDatabase, kind: ContentKind) {
  const s = new ContentService(db),
    rows = await s.table(kind).toArray(),
    drafts = (await db.contentDrafts.toArray()).filter(
      (d) => d.kind === kind && !d.deletedAt,
    );
  const tags = new Map((await db.tags.toArray()).map((t) => [t.id, t.name])),
    out: {
      item: Content;
      draft?: ContentDraft;
      conflict: boolean;
      tags: string[];
    }[] = [];
  for (const row of rows) {
    if (row.deletedAt) continue;
    const compatible = drafts
      .filter((d) => d.targetId === row.id && d.baseRevision === row.revision)
      .sort(
        (a, b) =>
          b.updatedAt.localeCompare(a.updatedAt) || a.id.localeCompare(b.id),
      );
    const d = compatible[0];
    out.push({
      item: d
        ? {
            ...d.payload,
            createdAt: row.createdAt,
            archivedAt: row.archivedAt,
            deletedAt: row.deletedAt,
          }
        : row,
      draft: d,
      conflict: false,
      tags: (d?.payload ?? row).tagIds.map((id) => tags.get(id) ?? "标签缺失"),
    });
  }
  for (const d of drafts.sort(
    (a, b) => b.revision - a.revision || a.id.localeCompare(b.id),
  )) {
    const row = rows.find((x) => x.id === d.targetId);
    if (row?.deletedAt) continue;
    if (out.some((x) => x.draft?.id === d.id)) continue;
    out.push({
      item: row
        ? {
            ...d.payload,
            createdAt: row.createdAt,
            archivedAt: row.archivedAt,
            deletedAt: row.deletedAt,
          }
        : d.payload,
      draft: d,
      conflict: !!row || out.some((x) => x.item.id === d.targetId),
      tags: d.payload.tagIds.map((id) => tags.get(id) ?? "标签缺失"),
    });
  }
  return out;
}
